// ============================================================================
// Field work Phase 1 — the time ledger, crew mode, rates and labour reports.
// Served by /api/field-work (HHNodeServer routes/fieldWorkTime.js).
// ============================================================================

import type { PayBasis, WorkerType } from './fieldWork';

export type TargetType = 'farm' | 'block' | 'cultivar_area';

export interface PlaceTarget {
  type: TargetType;
  id: number;
}

export type SegmentSource = 'crew' | 'self' | 'gps' | 'manual';

/** Someone on a job right now (crew mode). */
export interface OpenSegment {
  id: number;
  worker_id: number;
  started_at: string;
  target_type: TargetType | null;
  target_id: number | null;
  cost_centre_id: number | null;
  farm_id: number | null;
  block_id: number | null;
  cultivar_area_id: number | null;
  source: SegmentSource;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  picker_code: string;
  worker_type: WorkerType;
  place_name: string | null;
}

export interface WorkSegment {
  id: number;
  worker_id: number;
  job_id: number | null;
  job_name: string | null;
  activity_type_id: number | null;
  activity_type_name: string | null;
  target_type: TargetType | null;
  target_id: number | null;
  farm_id: number | null;
  farm_name: string | null;
  block_id: number | null;
  block_name: string | null;
  cultivar_area_id: number | null;
  cultivar_area_name: string | null;
  cost_centre_id: number | null;
  cost_centre_name: string | null;
  labour_provider_name: string | null;
  crew_name: string | null;
  started_at: string;
  ended_at: string | null;
  break_minutes: number;
  units_done: string | null;
  unit: string | null;
  source: SegmentSource;
  notes: string | null;
  edit_reason: string | null;
  voided_at: string | null;
  void_reason: string | null;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  picker_code: string;
  hours: string;
}

export type CrewActionKind = 'start' | 'switch' | 'stop';

export interface CrewAction {
  client_uuid: string;
  action: CrewActionKind;
  worker_ids?: number[];
  target?: PlaceTarget | null;
  cost_centre_id?: number | null;
  at?: string;
}

export interface CrewActionResult {
  client_uuid: string;
  status: 'applied' | 'duplicate' | 'rejected';
  affected?: number;
  error?: string;
  code?: string;
}

export interface CostCentre {
  id: number;
  name: string;
  code: string | null;
  farm_id: number | null;
  farm_name: string | null;
  active: boolean;
}

/** All org-entered: the org prices its own budgets (no platform wage data). */
export interface LabourSettings {
  currency: string;
  on_cost_pct: number;
  tax_name: string;
  tax_rate_pct: number;
  costs_include_tax: boolean;
}

/** Never per person: a farm manager must not see what individuals are paid. */
export type RateAppliesTo = 'worker_type' | 'labour_provider';
export type RateUnit = 'trees' | 'rows' | 'bins' | 'kg' | 'tonnes' | 'loads' | 'bales' | 'hectares';

export interface Rate {
  id: number;
  applies_to: RateAppliesTo;
  worker_type: WorkerType | null;
  labour_provider_id: number | null;
  labour_provider_name: string | null;
  activity_type_id: number | null;
  activity_type_name: string | null;
  cultivar_id: number | null;
  cultivar_name: string | null;
  farm_id: number | null;
  farm_name: string | null;
  basis: PayBasis;
  unit: RateUnit | null;
  amount: number;
  includes_tax: boolean;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}

export interface RateInput {
  applies_to: RateAppliesTo;
  worker_type?: WorkerType;
  labour_provider_id?: number;
  activity_type_id?: number | null;
  farm_id?: number | null;
  basis: PayBasis;
  unit?: RateUnit | null;
  amount: number;
  includes_tax?: boolean;
  effective_from: string;
  effective_to?: string | null;
  notes?: string | null;
}

export type LabourGroupKey =
  | 'farm' | 'block' | 'cultivar_area' | 'cultivar' | 'activity' | 'worker'
  | 'provider' | 'crew' | 'cost_centre' | 'job' | 'date';

export interface LabourReportRow {
  [key: string]: unknown;
  hours: number;
  apportioned_hours: number;
  unrated_hours?: number;
  units: Record<string, number>;
  worker_count: number;
  segment_count: number;
  area_hectares?: number | null;
  hours_per_ha?: number | null;
  /** Bins × nominal kg × fill, until packhouse weights arrive. */
  est_tonnes?: number;
  est_tonnes_per_ha?: number | null;
  wage?: number;
  on_cost?: number;
  charge?: number;
  tax?: number;
  total?: number;
  total_inc_tax?: number;
  cost_per_ha?: number | null;
}

export interface LabourReport {
  currency: string;
  on_cost_pct: number;
  tax_name: string;
  tax_rate_pct: number;
  costs_include_tax: boolean;
  group_by: LabourGroupKey[];
  includes_costs: boolean;
  rows: LabourReportRow[];
  totals: LabourReportRow;
}

export interface TimesheetCheckRow {
  worker_id: number;
  worker_name: string;
  date: string;
  timesheet_hours: number;
  job_hours: number;
  unallocated_hours: number;
  still_clocked_in: boolean;
}

export interface ContractorStatement {
  provider: { id: number; name: string; business_number: string | null };
  from: string;
  to: string;
  currency: string;
  tax_name: string;
  rows: LabourReportRow[];
  by_worker: LabourReportRow[];
  totals: LabourReportRow;
}

/** Name + id pair for each grouping, matching the server's GROUP_KEYS. */
export const GROUP_FIELDS: Record<LabourGroupKey, { id: string; name: string; label: string }> = {
  farm: { id: 'farm_id', name: 'farm_name', label: 'Farm' },
  block: { id: 'block_id', name: 'block_name', label: 'Block' },
  cultivar_area: { id: 'cultivar_area_id', name: 'cultivar_area_name', label: 'Cultivar area' },
  cultivar: { id: 'cultivar_id', name: 'cultivar_name', label: 'Cultivar' },
  activity: { id: 'activity_type_id', name: 'activity_type_name', label: 'Type of work' },
  worker: { id: 'worker_id', name: 'worker_name', label: 'Person' },
  provider: { id: 'labour_provider_id', name: 'labour_provider_name', label: 'Labour provider' },
  crew: { id: 'crew_id', name: 'crew_name', label: 'Crew' },
  cost_centre: { id: 'cost_centre_id', name: 'cost_centre_name', label: 'Cost centre' },
  job: { id: 'job_id', name: 'job_name', label: 'Job' },
  date: { id: 'date', name: 'date', label: 'Date' },
};

export const PAY_BASIS_LABELS: Record<PayBasis, string> = {
  per_hour: 'per hour',
  per_day: 'per day',
  per_unit: 'per unit',
  per_ha: 'per hectare',
  flat: 'flat per job',
};
